/**
 * <s-signup-popup> — Timed email signup popup
 * Opens its <dialog> once after `delay` seconds, then stays hidden for
 * `snooze-days` after a visitor closes it, and permanently after they sign up.
 *
 * Attributes:
 *   delay        — seconds before opening (default 6)
 *   snooze-days  — days to stay hidden after closing (default 14)
 *   form-id      — id of the signup form, used to detect a successful post

 */
class SSignupPopup extends HTMLElement {
  static #KEY = 'rg-signup-popup';
  #dialog;
  #timer;
  #ac;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const formId = this.getAttribute('form-id');
    const returnedHere = Boolean(formId) && window.location.hash === `#${formId}`;

    if (returnedHere && this.querySelector('[data-signup-success]')) {
      this.#store({ done: true });
      this.#open();
      return;
    }
    if (returnedHere && this.querySelector('[data-signup-error]')) {
      this.#open();
      return;
    }

    if (!this.#shouldShow()) return;

    this.#dialog.addEventListener(
      'close',
      () => {
        if (!this.#read().done) this.#store({ closedAt: Date.now() });
      },
      { signal: this.#ac.signal }
    );

    const delay = Number(this.getAttribute('delay') || 6) * 1000;
    this.#timer = setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #open() {
    if (this.#dialog.open || document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
  }

  #shouldShow() {
    const state = this.#read();
    if (state.done) return false;
    if (!state.closedAt) return true;
    const snoozeMs = Number(this.getAttribute('snooze-days') || 14) * 86400000;
    return Date.now() - state.closedAt > snoozeMs;
  }

  #read() {
    try {
      return JSON.parse(localStorage.getItem(SSignupPopup.#KEY)) || {};
    } catch {
      return {};
    }
  }

  #store(state) {
    try {
      localStorage.setItem(SSignupPopup.#KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable: popup simply shows again next visit */
    }
  }
}

customElements.define('s-signup-popup', SSignupPopup);
